/**
 * The first-run question's answers (W2-14 onboarding-wizard; DESIGN.md §4.2): three experience choices plus the
 * language. The dialog returns `{ level, preset, locale }` (DialogMap['firstRun']); the Library applies it on the
 * first launch, and "Show the welcome question again" (`help.welcome`) applies it here with the same rules:
 *  - I'm new to charting: Guided level, Default keys, sentence hints, the beginner tour;
 *  - I've used RPE: Full level, RPE keys and RPE editing conventions (immediate paste, `12.1` = 12 + 1/snap, raw
 *    RPE units), compact key hints, no beginner tour;
 *  - I've used other editors: Full level, Default keys and conventions, compact key hints, no beginner tour.
 * Nothing defaults to Full for newcomers; first-use tips stay on for everyone.
 */

import type { DialogMap } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import type { Locale } from '../../core/util/types';

export type FirstRunAnswer = NonNullable<DialogMap['firstRun']['result']>;
export type FirstRunChoice = 'new' | 'rpe' | 'other';

export const FIRST_RUN_CHOICES: readonly FirstRunChoice[] = ['new', 'rpe', 'other'];
export const LOCALES: readonly Locale[] = ['en', 'zh-CN'];

/** The dialog's result for a choice and a language. */
export function answerOf(choice: FirstRunChoice, locale: Locale): FirstRunAnswer {
  switch (choice) {
    case 'new':
      return { level: 'guided', preset: 'default', locale };
    case 'rpe':
      return { level: 'full', preset: 'rpe', locale };
    case 'other':
      return { level: 'full', preset: 'default', locale };
  }
}

/** The choice an answer corresponds to (for showing the current settings when the question is asked again). */
export function choiceOf(level: FirstRunAnswer['level'], preset: FirstRunAnswer['preset']): FirstRunChoice {
  if (level === 'guided') return 'new';
  return preset === 'rpe' ? 'rpe' : 'other';
}

/**
 * Writes an answer into the settings (SettingsService.applyFirstRun: the one mapping of the answer, CR-W2-14-4) and
 * switches the UI language at once.
 */
export function applyFirstRunAnswer(services: Pick<AppServices, 'settings' | 'i18n'>, answer: FirstRunAnswer): void {
  services.settings.applyFirstRun(answer);
  if (services.i18n.locale !== answer.locale) services.i18n.setLocale(answer.locale);
}
